import React, { useEffect, useState } from "react";
import { Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ExternalLink,
  HelpCircle,
  FileText,
  Code2,
  Users,
  Award,
  Package,
} from "lucide-react";
import { AppButton } from "../../ui";
import { fetchSessionEvaluation } from "../../../utils/smartLearning";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); sessionEvaluation.scss deleted
// with it. Its palette was three **Sass variables** — `$sev-accent: #7c3aed`,
// `$sev-accent-soft: rgba(124,58,237,.1)`, `$sev-green: #16a34a` — the second
// sheet in this batch to keep its colours that way. A Sass variable compiles to
// a literal, so it follows neither the theme nor the institute's brand; the
// accent is `--primary` now and the green is `--success`.
//
// The three result pills wrote their own tints and inks (`rgba(22,163,74,.12)` /
// `#15803d`, and a `#2563eb` blue for "info"). They are the status families,
// which is the same pairing every other pill in the app already uses.
const Q_CARD =
  "tw-rounded tw-border tw-border-line tw-bg-surface-raised tw-px-3.5 tw-py-3";
const Q_NUM =
  "tw-shrink-0 tw-rounded-[6px] tw-bg-primary/10 tw-px-[0.4rem] tw-py-[0.1rem] " +
  "tw-text-[0.7rem] tw-font-bold tw-text-primary";

const OPT =
  "tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-[0.3rem] tw-text-[0.82rem] " +
  "tw-text-content-muted [&_svg]:tw-shrink-0 [&_svg]:tw-text-success";
const OPT_CORRECT = "tw-bg-success/[0.08] tw-font-medium tw-text-content";

const EXPLAIN =
  "tw-mt-2 tw-text-[0.78rem] tw-leading-[1.45] tw-text-content-muted [&_strong]:tw-text-content";

const CHIP =
  "tw-inline-flex tw-items-center tw-gap-[0.3rem] tw-rounded-full tw-bg-surface-alt " +
  "tw-px-[0.6rem] tw-py-1 tw-text-[0.74rem] tw-font-medium tw-text-content-muted " +
  "[&_svg]:tw-text-primary";

const TABS =
  "tw-mb-3.5 tw-inline-flex tw-gap-1 tw-rounded-md tw-bg-surface-alt tw-p-[0.2rem]";
const TAB =
  "tw-cursor-pointer tw-rounded-sm tw-border-0 tw-bg-transparent tw-px-[0.8rem] tw-py-[0.4rem] " +
  "tw-text-[0.8rem] tw-font-medium tw-text-content-muted";
const TAB_ACTIVE = "tw-bg-surface-raised tw-text-content tw-shadow-card";

// Generated rows, so the head/cell rules are arbitrary variants on the table.
const TABLE =
  "tw-text-[0.82rem] [&_th]:tw-whitespace-nowrap [&_th]:tw-border-b-2 [&_th]:tw-text-[0.68rem] " +
  "[&_th]:tw-font-semibold [&_th]:tw-uppercase [&_th]:tw-tracking-[0.04em] " +
  "[&_th]:tw-text-content-muted [&_td]:tw-align-middle [&_td]:tw-text-content";

const PILL =
  "tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-[0.12rem] tw-text-[0.68rem] " +
  "tw-font-semibold tw-capitalize";
const PILL_PASS = "tw-bg-success-soft tw-text-success-fg";
const PILL_FAIL = "tw-bg-danger-soft tw-text-danger-fg";
const PILL_INFO = "tw-bg-info-soft tw-text-info-fg";

const MODAL_ICON =
  "tw-inline-flex tw-h-[30px] tw-w-[30px] tw-shrink-0 tw-items-center tw-justify-center " +
  "tw-rounded-sm tw-bg-primary/10 tw-text-primary";

const ERROR =
  "tw-rounded-md tw-bg-danger-soft tw-p-3 tw-text-[0.85rem] tw-text-danger-fg";

const KIND_META = {
  quiz: { Icon: HelpCircle, label: "Quiz" },
  assignment: { Icon: FileText, label: "Assignment" },
  codingChallenge: { Icon: Code2, label: "Coding Challenge" },
  scorm: { Icon: Package, label: "SCORM" },
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "—";

const CHOICE_TYPES = ["single_choice", "multi_select", "true_false", "image_mcq"];

/* ── One quiz question with its correct answer revealed ── */
function QuestionCard({ q }) {
  return (
    <div className={Q_CARD}>
      <div className="tw-flex tw-items-baseline tw-gap-2">
        <span className={Q_NUM}>Q{q.order}</span>
        <span className="tw-flex-1 tw-text-sm tw-font-medium tw-text-content">{q.questionText}</span>
        <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-[0.7rem] tw-text-content-subtle">{q.marks} mark{q.marks === 1 ? "" : "s"}</span>
      </div>

      {CHOICE_TYPES.includes(q.questionType) && q.options?.length > 0 && (
        <ul className="tw-m-0 tw-mt-2.5 tw-flex tw-list-none tw-flex-col tw-gap-[0.3rem] tw-p-0">
          {q.options.map((o, i) => (
            <li key={i} className={`${OPT} ${o.isCorrect ? OPT_CORRECT : ""}`}>
              {o.isCorrect ? <CheckCircle2 size={14} /> : <span className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-rounded-full tw-border-[1.5px] tw-border-line-soft" />}
              <span>{o.text}</span>
            </li>
          ))}
        </ul>
      )}

      {!CHOICE_TYPES.includes(q.questionType) && (
        <div className="tw-mt-2.5 tw-flex tw-flex-col tw-gap-[0.15rem]">
          <span className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-subtle">Correct answer</span>
          <span className="tw-text-[0.85rem] tw-font-semibold tw-text-success">
            {q.questionType === "numeric_answer"
              ? (q.numericAnswer ?? "—")
              : (q.acceptedAnswers?.length ? q.acceptedAnswers.join("  /  ") : "—")}
          </span>
        </div>
      )}

      {q.explanation && (
        <div className={EXPLAIN}>
          <strong>Why:</strong> {q.explanation}
        </div>
      )}
    </div>
  );
}

/* ── Quiz evaluation: questions + answers, and student attempts ── */
function QuizEvaluation({ data }) {
  const [tab, setTab] = useState("questions");
  const questions = data.questions || [];
  const attempts = data.attempts || [];

  return (
    <>
      <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-gap-2">
        <span className={CHIP}><HelpCircle size={13} /> {questions.length} questions</span>
        <span className={CHIP}><Users size={13} /> {data.attempted}/{data.total} attempted</span>
        {data.passingPercentage != null && (
          <span className={CHIP}><Award size={13} /> Pass ≥ {data.passingPercentage}%</span>
        )}
      </div>

      <div className={TABS}>
        <button
          type="button"
          className={`${TAB} ${tab === "questions" ? TAB_ACTIVE : ""}`}
          onClick={() => setTab("questions")}
        >
          Questions &amp; Answers
        </button>
        <button
          type="button"
          className={`${TAB} ${tab === "attempts" ? TAB_ACTIVE : ""}`}
          onClick={() => setTab("attempts")}
        >
          Student Attempts ({attempts.length})
        </button>
      </div>

      {tab === "questions" ? (
        questions.length === 0 ? (
          <p className="tw-my-2 tw-text-[0.85rem] tw-text-content-subtle">
            This quiz draws random questions from a pool, so a fixed list isn&apos;t available.
          </p>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-3">
            {questions.map((q) => <QuestionCard key={q.order} q={q} />)}
          </div>
        )
      ) : attempts.length === 0 ? (
        <p className="tw-my-2 tw-text-[0.85rem] tw-text-content-subtle">No students have attempted this quiz yet.</p>
      ) : (
        <Table size="sm" responsive className={`${TABLE} mb-0`}>
          <thead>
            <tr>
              <th>Student</th>
              <th className="text-center">Score</th>
              <th className="text-center">%</th>
              <th className="text-center">Result</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((a, i) => (
              <tr key={i}>
                <td>{a.studentName}</td>
                <td className="text-center">{a.score}/{a.totalMarks}</td>
                <td className="text-center">{Math.round(a.percentage || 0)}%</td>
                <td className="text-center">
                  <span className={`${PILL} ${a.passed ? PILL_PASS : PILL_FAIL}`}>
                    {a.passed ? "Passed" : "Failed"}
                  </span>
                </td>
                <td>{fmtDate(a.submittedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

/* ── Assignment evaluation: submissions + link to full grading ── */
function AssignmentEvaluation({ data, onNavigate }) {
  const subs = data.submissions || [];
  return (
    <>
      <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-gap-2">
        <span className={CHIP}><Users size={13} /> {data.submitted}/{data.total} submitted</span>
        <span className={CHIP}>{data.graded} graded</span>
        {data.maxMarks != null && <span className={CHIP}>Max {data.maxMarks}</span>}
        {data.dueDate && <span className={CHIP}>Due {fmtDate(data.dueDate)}</span>}
      </div>

      {data.reviewHref && (
        <AppButton variant="outline" size="sm" className="tw-mb-3.5" onClick={() => onNavigate(data.reviewHref)}>
          <ExternalLink size={14} /> Open full grading view
        </AppButton>
      )}

      {subs.length === 0 ? (
        <p className="tw-my-2 tw-text-[0.85rem] tw-text-content-subtle">No submissions yet.</p>
      ) : (
        <Table size="sm" responsive className={`${TABLE} mb-0`}>
          <thead>
            <tr>
              <th>Student</th>
              <th className="text-center">Status</th>
              <th className="text-center">Marks</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.submissionId}>
                <td>{s.studentName}</td>
                <td className="text-center">
                  <span className={`${PILL} ${s.status === "graded" ? PILL_PASS : PILL_INFO}`}>
                    {s.status === "resubmit_requested" ? "Resubmit" : s.status}
                  </span>
                </td>
                <td className="text-center">{s.marks != null ? `${s.marks}${data.maxMarks != null ? `/${data.maxMarks}` : ""}` : "—"}</td>
                <td>{fmtDate(s.submittedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

/* ── Coding evaluation: best submission per student ── */
function CodingEvaluation({ data }) {
  const subs = data.submissions || [];
  return (
    <>
      <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-gap-2">
        <span className={CHIP}><Users size={13} /> {data.attempted}/{data.total} attempted</span>
        <span className={CHIP}><CheckCircle2 size={13} /> {data.solved} solved</span>
        {data.difficulty && <span className={CHIP}>{data.difficulty}</span>}
      </div>

      {subs.length === 0 ? (
        <p className="tw-my-2 tw-text-[0.85rem] tw-text-content-subtle">No submissions yet.</p>
      ) : (
        <Table size="sm" responsive className={`${TABLE} mb-0`}>
          <thead>
            <tr>
              <th>Student</th>
              <th className="text-center">Status</th>
              <th className="text-center">Tests</th>
              <th className="text-center">Score</th>
              <th>Language</th>
              <th>Last try</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s, i) => (
              <tr key={i}>
                <td>{s.studentName}</td>
                <td className="text-center">
                  <span className={`${PILL} ${s.status === "solved" ? PILL_PASS : s.status === "partially_solved" ? PILL_INFO : PILL_FAIL}`}>
                    {s.status === "partially_solved" ? "Partial" : s.status}
                  </span>
                </td>
                <td className="text-center">{s.passedCount ?? 0}/{s.totalCount ?? 0}</td>
                <td className="text-center">{Math.round(s.score || 0)}</td>
                <td>{s.language || "—"}</td>
                <td>{fmtDate(s.submittedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

/* ── SCORM evaluation: per-student progress / completion ── */
function ScormEvaluation({ data }) {
  const rows = data.rows || [];
  return (
    <>
      <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-gap-2">
        <span className={CHIP}><Users size={13} /> {data.attempted}/{data.total} started</span>
        <span className={CHIP}><CheckCircle2 size={13} /> {data.completed} completed</span>
        {data.scoCount > 1 && <span className={CHIP}>{data.scoCount} sections</span>}
      </div>

      {rows.length === 0 ? (
        <p className="tw-my-2 tw-text-[0.85rem] tw-text-content-subtle">No students have opened this content yet.</p>
      ) : (
        <Table size="sm" responsive className={`${TABLE} mb-0`}>
          <thead>
            <tr>
              <th>Student</th>
              <th className="text-center">Status</th>
              <th className="text-center">Progress</th>
              <th className="text-center">Score</th>
              <th className="text-center">Attempts</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.studentName}</td>
                <td className="text-center">
                  <span className={`${PILL} ${r.status === "completed" ? PILL_PASS : r.status === "in_progress" ? PILL_INFO : PILL_FAIL}`}>
                    {r.status === "in_progress" ? "In progress" : r.status === "completed" ? "Completed" : "Not started"}
                  </span>
                </td>
                <td className="text-center">{r.progressPct == null ? "—" : `${r.progressPct}%`}</td>
                <td className="text-center">{r.score == null ? "—" : r.score}</td>
                <td className="text-center">{r.attempts || 0}</td>
                <td>{fmtDate(r.lastAccessedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

/**
 * SessionEvaluationModal
 *
 * Trainer evaluation drawer for a single smart-session resource — quiz
 * (questions + correct answers + attempts), assignment (submissions + grading
 * link) or coding challenge (submissions). So the trainer can review and
 * evaluate everything for the session from one place.
 */
const SessionEvaluationModal = ({ show, onHide, sessionId, batchId, resource }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!show || !resource?.refId) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    setData(null);
    fetchSessionEvaluation(sessionId, { kind: resource.kind, refId: resource.refId, batchId })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.message || "Failed to load evaluation."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, sessionId, batchId, resource]);

  const meta = KIND_META[resource?.kind] || KIND_META.quiz;
  const Icon = meta.Icon;

  const goTo = (href) => { onHide(); navigate(href); };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="tw-flex tw-items-center tw-gap-2.5 tw-text-base">
          <span className={MODAL_ICON}><Icon size={18} /></span>
          <span>
            {meta.label}
            <span className="tw-mt-px tw-block tw-text-[0.78rem] tw-font-normal tw-text-content-muted">{resource?.title || data?.title || ""}</span>
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-2 tw-py-6 tw-text-[0.85rem] tw-text-content-subtle"><Spinner animation="border" size="sm" /> <span>Loading evaluation…</span></div>
        ) : err ? (
          <div className={ERROR}>{err}</div>
        ) : !data ? null : data.kind === "quiz" ? (
          <QuizEvaluation data={data} />
        ) : data.kind === "assignment" ? (
          <AssignmentEvaluation data={data} onNavigate={goTo} />
        ) : data.kind === "codingChallenge" ? (
          <CodingEvaluation data={data} />
        ) : data.kind === "scorm" ? (
          <ScormEvaluation data={data} />
        ) : (
          <p className="tw-my-2 tw-text-[0.85rem] tw-text-content-subtle">{data.message || "Nothing to evaluate."}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="outline" size="sm" onClick={onHide}>Close</AppButton>
      </Modal.Footer>
    </Modal>
  );
};

export default SessionEvaluationModal;
